import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import type { EventTag } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useCreateEvent, useUpdateEvent, type EventRecord } from '@/hooks/useLineData';
import { toDatetimeLocalValue, toTimestampOrNull } from '@/lib/firestoreTime';

interface FormValues {
  name: string;
  eventDate: string; // datetime-local
  capacity: number;
  tag: EventTag;
  zoomLink: string;
  zoomId: string;
  zoomPassword: string;
  overview: string;
  recommendedFor: string;
  participantBenefits: string;
  participantCode: string;
  published: boolean;
  // P4: 参加実数の内訳（手入力）
  attOnsite: number;
  attOnline: number;
  attArchive: number;
}

const defaults: FormValues = {
  name: '',
  eventDate: '',
  capacity: 0,
  tag: 'online',
  zoomLink: '',
  zoomId: '',
  zoomPassword: '',
  overview: '',
  recommendedFor: '',
  participantBenefits: '',
  participantCode: '',
  published: true,
  attOnsite: 0,
  attOnline: 0,
  attArchive: 0,
};

interface EventFormDialogProps {
  open: boolean;
  existing: EventRecord | null;
  onOpenChange: (open: boolean) => void;
}

export function EventFormDialog({ open, existing, onOpenChange }: EventFormDialogProps) {
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();
  const form = useForm<FormValues>({ defaultValues: defaults });

  useEffect(() => {
    if (existing) {
      form.reset({
        name: existing.name ?? '',
        eventDate: toDatetimeLocalValue(existing.eventDate),
        capacity: existing.capacity ?? 0,
        tag: existing.tag ?? 'online',
        zoomLink: existing.zoomLink ?? '',
        zoomId: existing.zoomId ?? '',
        zoomPassword: existing.zoomPassword ?? '',
        overview: existing.overview ?? '',
        recommendedFor: existing.recommendedFor ?? '',
        participantBenefits: existing.participantBenefits ?? '',
        participantCode: existing.participantCode ?? '',
        published: !!existing.publishedAt,
        attOnsite: existing.attendanceBreakdown?.onsite ?? 0,
        attOnline: existing.attendanceBreakdown?.online ?? 0,
        attArchive: existing.attendanceBreakdown?.archive ?? 0,
      });
    } else if (open) {
      form.reset(defaults);
    }
  }, [existing, open, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      // P4: 参加実数の内訳を 3 値で持ち、currentAttendees はその合計を保存
      const onsite = Math.max(0, Number(values.attOnsite) || 0);
      const online = Math.max(0, Number(values.attOnline) || 0);
      const archive = Math.max(0, Number(values.attArchive) || 0);
      const attendanceBreakdown = { onsite, online, archive };
      const currentAttendees = onsite + online + archive;

      const payload = {
        name: values.name,
        eventDate: toTimestampOrNull(values.eventDate || null),
        capacity: Number(values.capacity) || 0,
        tag: values.tag,
        zoomLink: values.zoomLink || null,
        zoomId: values.zoomId || null,
        zoomPassword: values.zoomPassword || null,
        overview: values.overview,
        recommendedFor: values.recommendedFor,
        participantBenefits: values.participantBenefits,
        participantCode: values.participantCode || null,
        publishedAt: values.published ? new Date() : null,
        attendanceBreakdown,
        currentAttendees,
      } as Parameters<typeof createMutation.mutateAsync>[0];

      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, data: payload });
        toast.success('イベントを更新しました');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('イベントを作成しました');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? 'イベントを編集' : 'イベントを新規作成'}</DialogTitle>
          <DialogDescription>
            LINE Bot の query_events で参照されるデータです
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="e-name">イベント名</Label>
            <Input id="e-name" {...form.register('name', { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="e-date">開催日時</Label>
              <Input id="e-date" type="datetime-local" {...form.register('eventDate')} />
            </div>
            <div>
              <Label htmlFor="e-cap">定員（0 = 無制限）</Label>
              <Input id="e-cap" type="number" min={0} {...form.register('capacity')} />
            </div>
          </div>
          <div>
            <Label>開催形式</Label>
            <Select
              value={form.watch('tag')}
              onValueChange={(v) => form.setValue('tag', v as EventTag)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">オンライン</SelectItem>
                <SelectItem value="onsite">会場</SelectItem>
                <SelectItem value="both">ハイブリッド</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="e-overview">概要</Label>
            <Textarea id="e-overview" rows={3} {...form.register('overview')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="e-rec">おすすめの方</Label>
              <Textarea id="e-rec" rows={2} {...form.register('recommendedFor')} />
            </div>
            <div>
              <Label htmlFor="e-ben">参加特典</Label>
              <Textarea id="e-ben" rows={2} {...form.register('participantBenefits')} />
            </div>
          </div>
          <div className="border-t border-[var(--border)] pt-4">
            <h4 className="text-sm font-semibold mb-3">ZOOM 情報（参加者コード入力時に返却）</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="e-code">参加者コード</Label>
                <Input id="e-code" {...form.register('participantCode')} />
              </div>
              <div>
                <Label htmlFor="e-zid">ミーティング ID</Label>
                <Input id="e-zid" {...form.register('zoomId')} />
              </div>
              <div className="col-span-2">
                <Label htmlFor="e-zlink">ZOOM リンク</Label>
                <Input id="e-zlink" type="url" {...form.register('zoomLink')} />
              </div>
              <div>
                <Label htmlFor="e-zpw">パスワード</Label>
                <Input id="e-zpw" {...form.register('zoomPassword')} />
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--border)] pt-4">
            <h4 className="text-sm font-semibold mb-1">参加実数（手入力）</h4>
            <p className="text-[11px] text-[var(--text-muted)] mb-3">
              キャンペーンモニタリングの参加率算出に使われます。3 値の合計が表示用の参加実数になります。
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="e-att-onsite">会場 (offline)</Label>
                <Input
                  id="e-att-onsite"
                  type="number"
                  min={0}
                  {...form.register('attOnsite', { valueAsNumber: true })}
                />
              </div>
              <div>
                <Label htmlFor="e-att-online">オンライン (live)</Label>
                <Input
                  id="e-att-online"
                  type="number"
                  min={0}
                  {...form.register('attOnline', { valueAsNumber: true })}
                />
              </div>
              <div>
                <Label htmlFor="e-att-archive">アーカイブ視聴</Label>
                <Input
                  id="e-att-archive"
                  type="number"
                  min={0}
                  {...form.register('attArchive', { valueAsNumber: true })}
                />
              </div>
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-2">
              合計（参加実数）:{' '}
              {(Number(form.watch('attOnsite')) || 0) +
                (Number(form.watch('attOnline')) || 0) +
                (Number(form.watch('attArchive')) || 0)}{' '}
              名
            </div>
          </div>

          <Switch
            label="公開する"
            description="オフにすると LINE Bot から参照されなくなります"
            checked={form.watch('published')}
            onChange={(e) => form.setValue('published', e.target.checked)}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              キャンセル
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? '保存中...' : existing ? '更新' : '作成'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
